import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './AiChatWidget.css';

const quickPrompts = [
  'I need website development',
  'Show mobile app services',
  'Do you offer AI automation?',
  'Show contact details',
];

const replies = [
  {
    keywords: ['website', 'web', 'development', 'react', 'next'],
    text: 'Vedanskrix Tech builds responsive websites, portals, dashboards, and custom web applications for modern businesses.',
    links: [
      { to: '/services', label: 'Explore Services' },
      { to: '/contact', label: 'Request Quote' },
    ],
  },
  {
    keywords: ['mobile', 'app', 'android', 'ios', 'flutter'],
    text: 'We provide Android, iOS, Flutter, and React Native app development with launch-ready workflows.',
    links: [
      { to: '/services', label: 'See App Services' },
      { to: '/contact', label: 'Discuss App Project' },
    ],
  },
  {
    keywords: ['ai', 'automation', 'chatbot', 'openai'],
    text: 'Yes, Vedanskrix Tech supports AI integration, chatbots, workflow automation, and business process improvements.',
    links: [
      { to: '/services', label: 'View AI Services' },
      { to: '/contact', label: 'Talk To Tech Team' },
    ],
  },
];

function buildReply(message) {
  const query = message.toLowerCase();

  if (/(hi|hello|hey|namaste)/.test(query)) {
    return {
      text: 'Hello! I can help with technology services, portfolio direction, project discussions, and contact details.',
      links: [{ to: '/services', label: 'Open Services' }],
    };
  }

  if (/(contact|call|phone|email)/.test(query)) {
    return {
      text: 'You can reach Vedanskrix Tech at +91 87586 37022 or through the contact page for project discussions.',
      links: [
        { to: '/contact', label: 'Open Contact Page' },
        { href: 'tel:+918758637022', label: 'Call Now' },
      ],
    };
  }

  const match = replies.find((item) => item.keywords.some((keyword) => query.includes(keyword)));
  if (match) {
    return { text: match.text, links: match.links };
  }

  return {
    text: 'I can help with web development, mobile apps, AI automation, design, and digital product discussions.',
    links: [
      { to: '/services', label: 'Browse Services' },
      { to: '/portfolio', label: 'View Portfolio' },
      { to: '/contact', label: 'Send Enquiry' },
    ],
  };
}

function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      sender: 'assistant',
      text: 'Welcome to Vedanskrix Tech. Ask about services, portfolio, or project planning.',
    },
  ]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = (rawMessage) => {
    const text = rawMessage.trim();
    if (!text) {
      return;
    }

    const reply = buildReply(text);
    setMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, sender: 'user', text },
      { id: `assistant-${Date.now() + 1}`, sender: 'assistant', text: reply.text, links: reply.links },
    ]);
    setInputValue('');
    setIsOpen(true);
  };

  return (
    <div className={isOpen ? 'ai-chat-widget open' : 'ai-chat-widget'}>
      {isOpen ? (
        <section className="ai-chat-panel" aria-label="Vedanskrix Tech AI chat panel">
          <div className="ai-chat-header">
            <div className="ai-chat-brand">
              <img src="/logo.jpg" alt="Vedanskrix logo" />
              <div>
                <strong>Vedanskrix Tech AI</strong>
                <span>Project guidance, services, and quick support</span>
              </div>
            </div>
            <button type="button" className="ai-chat-close" aria-label="Close AI chat" onClick={() => setIsOpen(false)}>
              x
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={message.sender === 'assistant' ? 'ai-chat-bubble assistant' : 'ai-chat-bubble user'}>
                <p>{message.text}</p>
                {message.links?.length ? (
                  <div className="ai-chat-links">
                    {message.links.map((link) =>
                      link.to ? (
                        <Link key={`${message.id}-${link.label}`} to={link.to}>
                          {link.label}
                        </Link>
                      ) : (
                        <a key={`${message.id}-${link.label}`} href={link.href}>
                          {link.label}
                        </a>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-prompts">
            {quickPrompts.map((prompt) => (
              <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>
                {prompt}
              </button>
            ))}
          </div>

          <form
            className="ai-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage(inputValue);
            }}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Ask about tech services..."
              aria-label="Type your question"
            />
            <button type="submit">Send</button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="ai-chat-launcher"
        aria-expanded={isOpen}
        aria-label="Open Vedanskrix Tech AI chat"
        onClick={() => setIsOpen((current) => !current)}
      >
        <img src="/logo.jpg" alt="Vedanskrix logo" />
        <span>AI Chat</span>
      </button>
    </div>
  );
}

export default AiChatWidget;
